import {
    AutocompleteInput,
    Create,
    DateInput,
    ReferenceInput,
    SelectInput,
    SimpleForm,
    TextInput
} from '@/components/admin';
import { Card, CardContent } from '@/components/ui/card';
import { useDataProvider } from 'ra-core';
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { AutoReferenceField } from '../documents/shared/AutoReferenceField';

// When a GRN is selected, auto-fetch its vendor, linked PO and (via PO -> SO)
// the originating quotation, so the bill can't be linked to unrelated docs.
// See bug tracker VB #10.
const GRNLinker = () => {
    const grnId = useWatch({ name: 'grn_id' });
    const { setValue } = useFormContext();
    const dataProvider = useDataProvider();

    useEffect(() => {
        if (!grnId) return;
        let cancelled = false;

        (async () => {
            try {
                const { data: grn } = await dataProvider.getOne('goods_receipts', { id: grnId });
                if (cancelled || !grn) return;
                if (grn.vendor_id) setValue('vendor_id', grn.vendor_id, { shouldDirty: true });
                if (grn.po_id) {
                    setValue('po_id', grn.po_id, { shouldDirty: true });

                    // PO -> Sales Order -> Quotation
                    const { data: po } = await dataProvider.getOne('purchase_orders', { id: grn.po_id });
                    if (cancelled) return;
                    if (po?.sales_order_id) {
                        const { data: so } = await dataProvider.getOne('sales_orders', { id: po.sales_order_id });
                        if (!cancelled && so?.quotation_id) {
                            setValue('quotation_id', so.quotation_id, { shouldDirty: true });
                        }
                    }
                }
            } catch {
                /* best effort — leave fields for manual entry */
            }
        })();

        return () => { cancelled = true; };
    }, [grnId, dataProvider, setValue]);

    return null;
};

// PO dropdown scoped to the selected vendor so unrelated POs can't be picked.
const VendorScopedPOInput = ({ disabled }: { disabled?: boolean }) => {
    const vendorId = useWatch({ name: 'vendor_id' });
    return (
        <ReferenceInput
            source="po_id"
            reference="purchase_orders"
            perPage={100}
            sort={{ field: 'date', order: 'DESC' }}
            filter={vendorId ? { vendor_id: vendorId } : {}}
        >
            <AutocompleteInput optionText="reference" label="Purchase Order" disabled={disabled} />
        </ReferenceInput>
    );
};

export const VendorBillCreate = () => {
    const location = useLocation();
    const stateRecord = location.state?.record || {};

    return (
        <Create title="Record Vendor Bill" redirect="show" record={stateRecord}>
            <SimpleForm className="max-w-3xl">
                <GRNLinker />
                <Card className="w-full mb-4">
                    <CardContent className="pt-6">
                        <h3 className="text-lg font-bold text-slate-700 mb-4">
                            Bill Details
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <AutoReferenceField docType="vendor_bill" />
                            <ReferenceInput source="vendor_id" reference="vendors" perPage={100}>
                                <AutocompleteInput
                                    optionText="name"
                                    isRequired
                                    disabled={!!stateRecord.vendor_id}
                                    label="Vendor"
                                />
                            </ReferenceInput>

                            <DateInput source="bill_date" isRequired defaultValue={new Date().toISOString().split('T')[0]} />
                            <DateInput source="due_date" />

                            <ReferenceInput source="grn_id" reference="goods_receipts" perPage={100} sort={{ field: 'received_date', order: 'DESC' }}>
                                <AutocompleteInput optionText="reference" label="GRN" disabled={!!stateRecord.grn_id} />
                            </ReferenceInput>

                            <VendorScopedPOInput disabled={!!stateRecord.po_id} />

                            <ReferenceInput source="quotation_id" reference="quotations" perPage={100}>
                                <AutocompleteInput optionText="reference" label="Quotation (auto from GRN)" readOnly />
                            </ReferenceInput>

                            <SelectInput source="status" isRequired defaultValue="draft" choices={[
                                { id: 'draft', name: 'Draft' },
                                { id: 'approved', name: 'Approved' },
                            ]} />

                            <TextInput source="contact_person" label="Contact Person" />
                        </div>

                        <div className="mt-4">
                            <TextInput source="notes" multiline rows={3} />
                        </div>
                    </CardContent>
                </Card>
            </SimpleForm>
        </Create>
    );
};
